import { useState, useRef, useCallback } from "react";
import { Volume2, Loader2, Lock } from "lucide-react";
import { getAudioForWord } from "@/hooks/useDirectApi";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { cn } from "@/lib/utils";

// In-memory cache: avoid re-fetching the same word's audio during a session
const audioCache = new Map<string, string>();

interface AudioButtonProps {
  text: string;
  size?: "sm" | "md";
  className?: string;
  proOnly?: boolean;
}

export function AudioButton({ text, size = "md", className, proOnly = false }: AudioButtonProps) {
  const [state, setState] = useState<"idle" | "loading" | "playing">("idle");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { isPro, openUpgradePrompt } = useSubscription();

  const isLocked = proOnly && !isPro;

  const play = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLocked) {
      openUpgradePrompt("grammar");
      return;
    }

    if (state === "loading") return;

    // If already playing, restart
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    try {
      let base64 = audioCache.get(text);

      if (!base64) {
        setState("loading");
        base64 = await getAudioForWord(text);
        audioCache.set(text, base64);
      }

      setState("playing");
      const audio = new Audio(`data:audio/mp3;base64,${base64}`);
      audioRef.current = audio;
      audio.onended = () => setState("idle");
      audio.onerror = () => setState("idle");
      await audio.play();
    } catch (err) {
      console.error("Audio playback failed:", err);
      setState("idle");
    }
  }, [text, state, isLocked, openUpgradePrompt]);

  const iconSize = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
  const lockSize = size === "sm" ? "h-2 w-2" : "h-2.5 w-2.5";
  const buttonSize = size === "sm" ? "p-1" : "p-1.5";

  return (
    <button
      onClick={play}
      className={cn(
        "rounded-full hover:bg-muted transition-colors flex-shrink-0 relative",
        buttonSize,
        state === "playing" && "text-primary",
        isLocked && "opacity-60",
        className
      )}
      title={isLocked ? "Pro feature — upgrade to listen" : "Play pronunciation"}
      type="button"
    >
      {state === "loading" ? (
        <Loader2 className={cn(iconSize, "animate-spin text-muted-foreground")} />
      ) : (
        <>
          <Volume2 className={cn(iconSize, state === "playing" ? "text-primary" : "text-muted-foreground")} />
          {isLocked && (
            <Lock className={cn(lockSize, "absolute -bottom-0.5 -right-0.5 text-amber-600")} />
          )}
        </>
      )}
    </button>
  );
}
